import type { DirectoryMember } from "@closer/shared";
import { Button, Card, FormField, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * SAĞLAYICI İÇİ DAĞITIM (CLOS-1185).
 *
 * ### ⚠ NEDEN AYRI BİR YÜZEY
 *
 * Atama sağlayıcı ORGANİZASYONUNA dış tavan açıyor; büro içinde kimin gireceğini büronun
 * kendisi belirliyor. Bu kart olmadan kural işe yaramaz bir kural olurdu: pratikte yalnız
 * yöneticiler girebilir, ekipteki avukata dağıtım yapılamazdı.
 *
 * ### ⚠ DIŞ TAVANI GENİŞLETMİYOR
 *
 * Buradan verilen satır yalnız "büro içinde kim" sorusunu cevaplıyor. Kişinin NE
 * yapabileceği yine dört çarpanlı kesişimden geliyor ve hizmet alanın tavanını aşamıyor.
 *
 * ### ⚠ SÖZLEŞME SAHİBİ BU LİSTEYİ GÖRMÜYOR
 *
 * Büronun kimleri görevlendirdiği büronun özel bilgisi; satır sağlayıcının kendi
 * kiracısında yaşıyor ve karşı tarafa giden hiçbir yüke girmiyor.
 */
export function ProviderDistributionSection({
  contractId,
  /** Yalnız sağlayıcı yüzeyinde dolu: bu sözleşmede hangi taraf adına çalışıldığı. */
  servedPartyName,
}: {
  contractId: string;
  servedPartyName: string | null;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [secim, setSecim] = useState("");

  const uyeler = useQuery({
    queryKey: tenantKey(tenantId, "saglayici-ekip", contractId),
    enabled: activeTenantId !== null && can("organization.member"),
    queryFn: async () =>
      (await api<{ members: DirectoryMember[] }>(`/api/tenants/${tenantId}/members`, { tenantId }))
        .data.members,
  });

  const dagit = useMutation({
    mutationFn: (membershipId: string) =>
      api(`/api/shared-contracts/${contractId}/service-assignments/distribution`, {
        method: "POST",
        tenantId,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ membershipId }),
      }),
    onSuccess: async () => {
      setSecim("");
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "saglayici-ekip", contractId),
      });
      toast.success("Sözleşme ekip üyesine açıldı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Dağıtım yapılamadı"),
  });

  /**
   * ⚠ KART YALNIZ SAĞLAYICI YÜZEYİNDE. `servedPartyName` bu yükte yalnız sağlayıcıya
   * dolu geliyor; katılımcı organizasyon kendi adına hareket ettiği için orada boş.
   */
  if (!servedPartyName) return null;
  /** ⚠ Dağıtım paylaşma yetkisi istiyor; sunucu ayrıca yönetici olmayı da arıyor. */
  if (!can("contract.share")) return null;

  const aktifUyeler = (uyeler.data ?? []).filter((u) => u.status === "active");

  return (
    <Card title="Ekibinize açın">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-muted">
          Bu sözleşmede <span className="font-medium text-fg">{servedPartyName}</span> adına
          çalışıyorsunuz. Sözleşmeyi görecek ekip üyelerini burada belirlersiniz; bu liste karşı
          tarafa gitmez.
        </p>

        {aktifUyeler.length === 0 ? (
          <p className="text-xs text-fg-soft" data-testid="ekip-yok">
            Ekip listesi getirilemedi ya da başka aktif üye yok.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <FormField label="Ekip üyesi">
              <Select
                value={secim}
                data-testid="dagitim-uye"
                onChange={(o) => setSecim(o.target.value)}
              >
                <option value="">Seçiniz…</option>
                {aktifUyeler.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <Button
              type="button"
              size="sm"
              data-testid="dagitim-ver"
              loading={dagit.isPending}
              disabled={secim.length === 0}
              onClick={() => dagit.mutate(secim)}
            >
              Sözleşmeyi aç
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
